import { Book, WordPressBook } from "@/types/book";

// Use proxy in production to avoid CORS issues
const USE_PROXY = typeof window !== 'undefined' && 
  (window.location.hostname === 'faydabooks.site' || 
   window.location.hostname.includes('pages.dev'));

const WORDPRESS_API_URL = "https://faydabooks.diatech.consulting/wp-json/wp/v2";
const PROXY_API_URL = "/api/wordpress-proxy";

// Helper function to make API requests with or without proxy
async function makeWordPressRequest(path: string): Promise<Response> {
  if (USE_PROXY) {
    console.log(`Using proxy for WordPress API request: ${path}`);
    const proxyUrl = `${PROXY_API_URL}?path=${encodeURIComponent(path)}`;
    return fetch(proxyUrl);
  } else {
    console.log(`Making direct WordPress API request: ${path}`);
    return fetch(`https://faydabooks.diatech.consulting${path}`);
  }
}

// Helper function to get media URL from ID
async function getMediaUrl(mediaId: number | string): Promise<string | undefined> {
  if (!mediaId) {
    console.log('getMediaUrl: No mediaId provided');
    return undefined;
  }
  
  console.log(`getMediaUrl: Fetching media URL for ID ${mediaId}`);
  
  try {
    const mediaPath = `/wp-json/wp/v2/media/${mediaId}`;
    console.log(`getMediaUrl: Requesting ${mediaPath}`);
    
    const response = await makeWordPressRequest(mediaPath);
    if (!response.ok) {
      console.error(`getMediaUrl: HTTP ${response.status} ${response.statusText} for media ID ${mediaId}`);
      return undefined;
    }
    
    const media = await response.json();
    const sourceUrl = media.source_url;
    
    console.log(`getMediaUrl: Successfully resolved media ID ${mediaId} to URL: ${sourceUrl}`);
    
    if (!sourceUrl) {
      console.error(`getMediaUrl: No source_url found in media response for ID ${mediaId}`);
      return undefined;
    }
    
    return sourceUrl;
  } catch (error) {
    console.error(`getMediaUrl: Error fetching media URL for ID ${mediaId}:`, error);
    return undefined;
  }
}

export async function fetchWordPressBooks(): Promise<Book[]> {
  try {
    const response = await makeWordPressRequest('/wp-json/wp/v2/posts?_embed');
    if (!response.ok) {
      throw new Error("Failed to fetch books from WordPress");
    }
    
    const posts: any[] = await response.json();
    
    const books = await Promise.all(posts.map(async (post) => {
      let pdfUrl = post.acf?.pdf_file || post.acf?.pdf;
      let txtUrl = post.acf?.txt_file || post.acf?.txt;
      let audioUrl = post.acf?.audio_file || post.acf?.audio;
      let coverImage = post.acf?.cover_image;
      
      // If PDF is a number (file ID), fetch the actual URL
      if (pdfUrl && typeof pdfUrl === 'number') {
        pdfUrl = await getMediaUrl(pdfUrl);
      }
      
      // If TXT is a number (file ID), fetch the actual URL
      if (txtUrl && typeof txtUrl === 'number') {
        txtUrl = await getMediaUrl(txtUrl);
      }
      
      // If audio is a number (file ID), fetch the actual URL
      if (audioUrl && typeof audioUrl === 'number') {
        audioUrl = await getMediaUrl(audioUrl);
      }
      
      // If cover image is a number (file ID), fetch the actual URL
      if (coverImage && typeof coverImage === 'number') {
        coverImage = await getMediaUrl(coverImage);
      }
      
      return {
        id: post.id,
        title: post.title.rendered || "Untitled",
        author: post.acf?.author || post._embedded?.author?.[0]?.name || "Unknown Author",
        category: post.acf?.category || post._embedded?.["wp:term"]?.[0]?.[0]?.name || "Uncategorized",
        description: post.acf?.description || post.excerpt?.rendered?.replace(/<[^>]*>/g, "") || "",
        coverImage: coverImage || post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=600&fit=crop",
        pdfUrl,
        txtUrl,
        audioUrl,
        publishDate: post.date || new Date().toISOString(),
        downloads: post.acf?.downloads || 0,
        rating: post.acf?.rating || 0,
      };
    }));
    
    return books;
  } catch (error) {
    console.error("Error fetching WordPress books:", error);
    return [];
  }
}

export async function fetchWordPressBookById(id: number): Promise<Book | null> {
  try {
    console.log(`fetchWordPressBookById: Fetching book ID ${id}`);
    
    const response = await makeWordPressRequest(`/wp-json/wp/v2/posts/${id}?_embed`);
    if (!response.ok) {
      throw new Error("Failed to fetch book from WordPress");
    }
    
    const post: any = await response.json();
    
    console.log(`fetchWordPressBookById: Retrieved post data for "${post.title?.rendered}"`);
    console.log('fetchWordPressBookById: ACF fields:', post.acf);
    
    let pdfUrl = post.acf?.pdf_file || post.acf?.pdf;
    let txtUrl = post.acf?.txt_file || post.acf?.txt;
    let audioUrl = post.acf?.audio_file || post.acf?.audio;
    let coverImage = post.acf?.cover_image;
    
    console.log(`fetchWordPressBookById: Raw field values - PDF: ${pdfUrl}, TXT: ${txtUrl}, Audio: ${audioUrl}`);
    
    // If PDF is a number (file ID), fetch the actual URL
    if (pdfUrl && typeof pdfUrl === 'number') {
      console.log(`fetchWordPressBookById: Resolving PDF file ID ${pdfUrl} to URL`);
      pdfUrl = await getMediaUrl(pdfUrl);
      console.log(`fetchWordPressBookById: PDF resolved to: ${pdfUrl}`);
    }
    
    // If TXT is a number (file ID), fetch the actual URL
    if (txtUrl && typeof txtUrl === 'number') {
      console.log(`fetchWordPressBookById: Resolving TXT file ID ${txtUrl} to URL`);
      txtUrl = await getMediaUrl(txtUrl);
      console.log(`fetchWordPressBookById: TXT resolved to: ${txtUrl}`);
    }
    
    // If audio is a number (file ID), fetch the actual URL
    if (audioUrl && typeof audioUrl === 'number') {
      console.log(`fetchWordPressBookById: Resolving audio file ID ${audioUrl} to URL`);
      audioUrl = await getMediaUrl(audioUrl);
      console.log(`fetchWordPressBookById: Audio resolved to: ${audioUrl}`);
    }
    
    // If cover image is a number (file ID), fetch the actual URL
    if (coverImage && typeof coverImage === 'number') {
      coverImage = await getMediaUrl(coverImage);
    }
    
    const bookData = {
      id: post.id,
      title: post.title.rendered || "Untitled",
      author: post.acf?.author || post._embedded?.author?.[0]?.name || "Unknown Author",
      category: post.acf?.category || post._embedded?.["wp:term"]?.[0]?.[0]?.name || "Uncategorized",
      description: post.acf?.description || post.excerpt?.rendered?.replace(/<[^>]*>/g, "") || "",
      coverImage: coverImage || post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=600&fit=crop",
      pdfUrl,
      txtUrl,
      audioUrl,
      publishDate: post.date || new Date().toISOString(),
      downloads: post.acf?.downloads || 0,
      rating: post.acf?.rating || 0,
    };
    
    console.log(`fetchWordPressBookById: Final book data for "${bookData.title}":`);
    console.log(`- PDF URL: ${bookData.pdfUrl}`);
    console.log(`- TXT URL: ${bookData.txtUrl}`);
    console.log(`- Audio URL: ${bookData.audioUrl}`);
    
    return bookData;
  } catch (error) {
    console.error("Error fetching WordPress book:", error);
    return null;
  }
}
